// navGuard — lightweight runtime tripwire that detects navigation
// loops so we never ship a silent A↔B ping-pong bug again.
//
// How it works:
//   • useNavLoopGuard() sits at the app root and watches every
//     pathname transition via react-router's `useLocation`.
//   • It keeps a rolling window of the last N pathnames (with
//     timestamps). If the SAME pathname appears ≥ THRESHOLD times
//     within WINDOW_MS milliseconds, we assume the app is stuck.
//   • On trip: sonner toast to the user ("Er zit een lus in de app —
//     tik hier om terug te gaan naar Dashboard"), Sentry breadcrumb
//     with the full path chain, and a global block that ignores any
//     further programmatic navigation for COOLDOWN_MS so the loop
//     actually stops instead of continuing under the toast.
//   • The block is enforced by `safeNavigate()`, our thin wrapper
//     around react-router's `navigate`. Direct `<Link>` clicks still
//     work — those are user-initiated and part of the escape hatch.
//
// The guard is Sentry-aware but Sentry-optional: if Sentry isn't
// initialised the breadcrumb is silently dropped.
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

// ─── Tuning ────────────────────────────────────────────────────────
const WINDOW_MS = 2500;   // rolling window
const THRESHOLD = 3;      // 3× same path in the window = loop
const HISTORY   = 8;      // last N pathnames retained
const COOLDOWN_MS = 4000; // block programmatic navs for this long
// ───────────────────────────────────────────────────────────────────

// Module-level flag consulted by safeNavigate(). Not a React state on
// purpose — we don't want to re-render every subscriber when it flips.
let _blockedUntil = 0;
export function isNavBlocked() {
  return Date.now() < _blockedUntil;
}

/**
 * useNavLoopGuard — mount once at the app root.
 * On loop trip: toast + optional Sentry breadcrumb + programmatic-nav
 * cooldown. Returns nothing.
 */
export function useNavLoopGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const historyRef = useRef([]); // [{ path, at }]
  const lastToastAtRef = useRef(0);

  useEffect(() => {
    const now = Date.now();
    const path = location.pathname;
    historyRef.current.push({ path, at: now });
    if (historyRef.current.length > HISTORY) {
      historyRef.current.shift();
    }
    // Count occurrences of the current path within the window.
    const recent = historyRef.current.filter(e => now - e.at <= WINDOW_MS);
    const hits = recent.filter(e => e.path === path).length;

    if (hits >= THRESHOLD) {
      _blockedUntil = now + COOLDOWN_MS;
      // Rate-limit the toast (max 1 per 10s) so a hard loop doesn't
      // spam the same message repeatedly.
      if (now - lastToastAtRef.current > 10000) {
        lastToastAtRef.current = now;
        toast.error("Er zit een lus in de app 🌀", {
          description: "We hebben je terug naar Dashboard gestuurd.",
          duration: 6000,
          action: {
            label: "Ga naar Dashboard",
            onClick: () => navigate("/dashboard", { replace: true }),
          },
        });
        // Sentry breadcrumb (best-effort — never let this crash the guard).
        try {
          const Sentry = window.Sentry;
          if (Sentry && typeof Sentry.captureMessage === "function") {
            Sentry.captureMessage("Navigation loop detected", {
              level: "warning",
              tags: { nav_loop: "true", path },
              extra: {
                chain: historyRef.current.map(e => e.path).join(" → "),
                window_ms: WINDOW_MS,
                threshold: THRESHOLD,
              },
            });
          }
        } catch { /* swallow */ }
        // Auto-escape after a short beat so the user isn't stranded.
        setTimeout(() => {
          if (window.location.pathname === path) {
            navigate("/dashboard", { replace: true });
          }
        }, 350);
      }
    }
  }, [location.pathname, navigate]);
}

/**
 * safeNavigate — thin wrapper around navigate() that:
 *   1. respects the loop-cooldown (drops programmatic navs during it)
 *   2. dedupes same-tick "come back to where you just were" navs
 *      (route A → B → A within 500ms of leaving A → the last A is
 *      swallowed as a suspected boomerang).
 *
 * Callers should prefer this helper over raw navigate() for any nav
 * fired from inside useEffect / event handlers that touch server data.
 * Direct <Link>/<NavLink> clicks bypass it — those are user-initiated.
 */
const _recent = []; // [{ path, at }]
export function makeSafeNavigate(navigate) {
  return function safeNavigate(to, opts) {
    if (isNavBlocked()) {
      // eslint-disable-next-line no-console
      console.warn("[safeNavigate] blocked — loop cooldown active:", to);
      return;
    }
    const now = Date.now();
    const path = typeof to === "string" ? to : to?.pathname;
    if (path) {
      // Boomerang detector: nav(A) → nav(B) → nav(A) with each hop
      // < 500ms is almost certainly a bug.
      const last = _recent[_recent.length - 1];
      const prev = _recent[_recent.length - 2];
      if (last && prev && prev.path === path && now - last.at < 500) {
        // eslint-disable-next-line no-console
        console.warn("[safeNavigate] boomerang swallowed:",
                     prev.path, "→", last.path, "→", path);
        return;
      }
      _recent.push({ path, at: now });
      if (_recent.length > HISTORY) _recent.shift();
    }
    return navigate(to, opts);
  };
}
